/**
 * @file Client wrapper for the `queryAnalytics` callable: asks for one named
 * query and settles to an answered, refused or failed `AnalyticsSettledAnswer`,
 * never throwing. Consumer: `useAnalyticsAnswer`. A refusal is read off the
 * callable error's `details`, each field checked rather than asserted, and a
 * `refusal` outside `ANALYTICS_REFUSAL_CODES` counts as a failure. The answer
 * passes through `parseNamedQueryResult`, so a result outside the shared
 * contract fails instead of rendering.
 */
import { httpsCallable } from 'firebase/functions';

import { functions } from '@/services/firebaseFunctions';
import type { AnalyticsSettledAnswer } from '@/analytics';
import { ANALYTICS_REFUSAL_CODES, parseNamedQueryResult } from '#types';
import type { AnalyticsQueryRefusal, NamedQueryRequest } from '#types';

const refusalOf = (details: unknown): AnalyticsQueryRefusal | undefined => {
  if (
    typeof details !== 'object' ||
    details === null ||
    !('refusal' in details)
  )
    return undefined;
  const refusal = ANALYTICS_REFUSAL_CODES.find(
    (code) => code === details.refusal,
  );
  if (refusal === undefined) return undefined;
  return 'key' in details && typeof details.key === 'string'
    ? { refusal, key: details.key }
    : { refusal };
};

export const queryAnalytics = async (
  request: NamedQueryRequest,
): Promise<AnalyticsSettledAnswer> => {
  try {
    const callable = httpsCallable<NamedQueryRequest, unknown>(
      functions,
      'queryAnalytics',
    );
    const { data } = await callable(request);
    const result = parseNamedQueryResult(data);
    return result
      ? { status: 'answered', result }
      : {
          status: 'failed',
          message: 'Analytics answered in an unexpected shape.',
        };
  } catch (error) {
    if (!(error instanceof Error))
      return { status: 'failed', message: 'Analytics failed.' };
    const refusal = 'details' in error ? refusalOf(error.details) : undefined;
    return refusal
      ? { status: 'refused', refusal, message: error.message }
      : { status: 'failed', message: error.message };
  }
};
